<script setup lang="ts">
/**
 * The run/gate state badge — the single most repeated piece of meaning in the portal, and the
 * reference implementation of the BG §18.4 status colour system.
 *
 * Two brand rules shape it. BG §22.3: colour never carries meaning alone, so every pill renders a glyph
 * and a word as well as a hue, and stays legible in greyscale. BG §18.2: the palette has no red, so
 * failure takes magenta — "command, action, intensity" (BG §18.3) — and leans on its ✕ to say what
 * amber's ● and green's ✓ cannot say by colour alone.
 */
import { computed } from 'vue'
import type { StatusTone } from '../tokens'

type Tone = StatusTone

const props = withDefaults(
    defineProps<{
        tone: Tone
        /** Renders as a bare count chip — for sidebar badges, where the label is the nav item. */
        count?: number
        /** The pill still pulses when live; legends and dense table rows pass `pulse: false`. */
        pulse?: boolean
    }>(),
    { pulse: true },
)

/** BG §22.3 — the redundant channel. Never drop these: they are what makes the pill work without colour. */
const GLYPH: Record<Tone, string> = {
    running: '●',
    completed: '✓',
    // Action required. The triangle is the only glyph that points at you, which is the point.
    action: '▲',
    failed: '✕',
    neutral: '·',
    // Not a run state: cyan is the system channel (BG §18.3), for what the console itself is — the build serving it.
    system: '◆',
    /*
     * The billing plan — a label, not a state, so it takes dirty white rather than one of the five
     * status colours. The diamond-in-diamond keeps it distinct from system's ◆ at a glance, and the
     * plan name is spelled out beside it, which is what actually carries the meaning.
     */
    plan: '◈',
}

const glyph = computed(() => GLYPH[props.tone])
const live = computed(() => props.tone === 'running' && props.pulse)
</script>

<template>
    <span
        class="zh-status-pill"
        :class="`zh-status-pill--${tone}`"
        :data-live="live ? 'on' : 'off'"
        :data-count="count !== undefined ? 'on' : 'off'"
    >
        <span v-if="count === undefined" class="zh-status-pill__glyph" aria-hidden="true">{{ glyph }}</span>
        <slot>{{ count }}</slot>
    </span>
</template>

<style scoped>
.zh-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    white-space: nowrap;
    /* Square, per BG §17.2 — the pill-shaped badge is exactly the rounded lockup the brand rules out. */
    border: 1px solid currentColor;
    padding: 0.125rem 0.4375rem;
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.4;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
}

.zh-status-pill[data-count='on'] {
    justify-content: center;
    min-width: 1.25rem;
    padding: 0 0.375rem;
    font-variant-numeric: tabular-nums;
}

.zh-status-pill__glyph {
    font-size: 0.625rem;
}

.zh-status-pill--running {
    color: var(--state-active);
    background: color-mix(in srgb, var(--state-active) 10%, transparent);
}

.zh-status-pill--completed {
    color: var(--state-complete);
    background: color-mix(in srgb, var(--state-complete) 10%, transparent);
}

.zh-status-pill--action {
    color: var(--state-action);
    background: color-mix(in srgb, var(--state-action) 10%, transparent);
}

.zh-status-pill--failed {
    color: var(--state-failed);
    background: color-mix(in srgb, var(--state-failed) 10%, transparent);
}

.zh-status-pill--system {
    color: var(--state-system);
    background: color-mix(in srgb, var(--state-system) 10%, transparent);
}

.zh-status-pill--plan {
    color: var(--dirty-white);
    background: color-mix(in srgb, var(--dirty-white) 8%, transparent);
}

/*
 * Raw Muted Grey is 4.40:1 on System Charcoal, under WCAG AA for a 12px word. The word takes the
 * secondary-text step instead; the glyph keeps the grey, which is the inactive indicator (BG §18.4)
 * and only needs 3:1. src/lib/pairings.ts measures both.
 */
.zh-status-pill--neutral {
    color: var(--muted-foreground);
    background: color-mix(in srgb, var(--state-inactive) 8%, transparent);
    border-color: var(--border);
}

.zh-status-pill--neutral .zh-status-pill__glyph {
    color: var(--state-inactive);
}

.zh-status-pill[data-live='on'] .zh-status-pill__glyph {
    animation: zh-pill-blink var(--motion-status-blink) ease-in-out infinite;
}

@keyframes zh-pill-blink {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.35;
    }
}

@media (prefers-reduced-motion: reduce) {
    .zh-status-pill[data-live='on'] .zh-status-pill__glyph {
        animation: none;
        opacity: 1;
    }
}
</style>
